/**
 * Tilawah reading setup labels for the admin — same wording as the app
 * (mobile/lib/app/utils/tilawah_period.dart and its id locale strings), so
 * a group reads identically in both places.
 *
 * reading_period: 'day' | 'week' | 'month' (how often a turn comes)
 * reading_scope:  'member' (amount per member) | 'group' (split evenly)
 * reading_record: 'reader' (only via the in-app reader) | 'manual'
 * Missing values (older API / unmigrated DB) mean the original daily,
 * split-evenly, manual-marking behaviour.
 */

export type TilawahPeriod = 'day' | 'week' | 'month';

export function normalizePeriod(period : any) : TilawahPeriod {
  return period === 'week' || period === 'month' ? period : 'day';
}

/** "Day" / "Week" / "Month" — English, like the app's turn tag. */
export function turnWord(period : any) : string {
  const p = normalizePeriod(period);
  return p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'Day';
}

/** "#Day 3" / "#Week 3" / "#Month 3" (hash optional). */
export function turnTag(period : any, n : any, hash = true) : string {
  return `${hash ? '#' : ''}${turnWord(period)} ${n}`;
}

/** "hari" / "pekan" / "bulan". */
export function periodUnit(period : any) : string {
  const p = normalizePeriod(period);
  return p === 'week' ? 'pekan' : p === 'month' ? 'bulan' : 'hari';
}

/** Short label for the running turn: "hari ini" / "pekan ini" / "bulan ini". */
export function currentTurnLabel(period : any) : string {
  return `${periodUnit(period)} ini`;
}

/** "1 juz/anggota / pekan" or "2 juz / hari" — the app's target chip. */
export function targetLabel(g : any, formatNumber : (n : any) => string) : string {
  if(!g || !g.reading_amount){ return '—'; }
  const amount = `${formatNumber(g.reading_amount)} ${g.reading_category || ''}`.trim();
  const perMember = g.reading_scope === 'member' ? '/anggota' : '';
  return `${amount}${perMember} / ${periodUnit(g.reading_period)}`;
}

export function scopeLabel(scope : any) : string {
  return scope === 'member' ? 'Tiap Anggota' : 'Dibagi Rata';
}

export function recordLabel(record : any) : string {
  return record === 'reader' ? 'Wajib lewat Hatam' : 'Tandai manual';
}

/** Any turn tag inside chat text ("#Day 3", "#Week 3", "#Month 3"). */
export const TURN_TAG_SOURCE = '#(?:Day|Week|Month) \\d+';
